import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes, css } from "styled-components";
import { motion, AnimatePresence } from "framer-motion";

/* ─────────────────────────────────────────────────────
   ChatPreview — production-grade replacement for GIFs.
   Renders a WhatsApp-faithful chat panel with an animated
   conversation that types out when scrolled into view.
   ───────────────────────────────────────────────────── */

const TypingFrame = keyframes`
  0%, 60%, 100% { transform: translateY(0);   opacity: 0.35; }
  30%           { transform: translateY(-4px); opacity: 1;    }
`;

const Frame = styled.div`
  position: relative;
  width: 100%;
  max-width: 440px;
  margin: 0 auto;
  background: var(--wa-bg);
  border-radius: 18px;
  overflow: hidden;
  box-shadow:
    0 1px 0 rgba(14, 17, 22, 0.04),
    0 2px 6px -1px rgba(14, 17, 22, 0.04),
    0 24px 60px -24px rgba(14, 17, 22, 0.18);
  border: 1px solid var(--rule);
`;

/* Header */
const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: linear-gradient(180deg, var(--wa-green) 0%, var(--wa-green-d) 100%);
  color: #fff;
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.35);
  display: grid;
  place-items: center;
  font-family: var(--chat-sans);
  font-weight: 600;
  font-size: 1rem;
  color: #fff;
  flex-shrink: 0;
`;

const NameBlock = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.15;
  flex: 1;
  min-width: 0;
`;

const Name = styled.span`
  font-family: var(--chat-sans);
  font-weight: 600;
  font-size: 0.95rem;
  letter-spacing: -0.005em;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Status = styled.span`
  font-family: var(--chat-sans);
  font-size: 0.74rem;
  color: rgba(255, 255, 255, 0.85);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 3px;
  font-weight: 400;
`;

const Badge = styled.span`
  background: var(--wa-pill);
  color: var(--wa-ink);
  font-family: var(--mono);
  font-size: 10.5px;
  font-weight: 500;
  padding: 5px 10px;
  border-radius: 999px;
  letter-spacing: 0.04em;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 5px;

  &::before {
    content: "";
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--wa-green-d);
    display: inline-block;
  }
`;

/* Body */
const Body = styled.div`
  position: relative;
  height: 600px;
  padding: 18px 14px 22px;
  background-color: var(--wa-bg);
  background-image:
    radial-gradient(rgba(14, 17, 22, 0.025) 1px, transparent 1px);
  background-size: 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: hidden;

  @media (min-width: 460px) { height: 540px; }
  @media (min-width: 600px) { height: 495px; }
`;

/* Bubble rows */
const Row = styled(motion.div)`
  display: flex;
  width: 100%;
  ${(p) => p.$out
    ? css`justify-content: flex-end;`
    : css`justify-content: flex-start;`
  }
`;

const Bubble = styled.div`
  max-width: 82%;
  padding: 7px 10px 6px;
  font-family: var(--chat-sans);
  font-size: 0.9rem;
  line-height: 1.42;
  color: var(--wa-ink);
  position: relative;
  word-wrap: break-word;
  box-shadow: 0 1px 0 rgba(14, 17, 22, 0.06);
  letter-spacing: -0.005em;

  ${(p) => p.$out
    ? css`
        background: var(--wa-bubble-out);
        border-radius: 12px 0 12px 12px;
      `
    : css`
        background: var(--wa-bubble-in);
        border-radius: 0 12px 12px 12px;
      `
  }

  strong, b { font-weight: 600; }

  .author {
    display: block;
    font-size: 0.78rem;
    font-weight: 600;
    color: #1d77f2;
    margin-bottom: 2px;
    letter-spacing: -0.005em;
  }

  .body { display: block; }

  .meta {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 10.5px;
    color: rgba(14, 17, 22, 0.45);
    float: right;
    margin: 6px 0 -2px 8px;
    line-height: 1;
  }

  /* Clearfix for floated meta */
  &::after {
    content: "";
    display: table;
    clear: both;
  }
`;

const Tick = styled.svg`
  width: 14px;
  height: 10px;
  color: ${(p) => (p.$read ? "var(--wa-tick)" : "rgba(14, 17, 22, 0.35)")};
`;

function Ticks({ read = true }) {
  return (
    <Tick $read={read} viewBox="0 0 16 11" fill="none">
      <path d="M11.071 1L4.5 7.571 1.929 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15 1L8.429 7.571" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </Tick>
  );
}

/* Typing pill — side-aware (left=incoming/white, right=outgoing/green) */
const TypingPill = styled(motion.div)`
  align-self: ${(p) => (p.$out ? "flex-end" : "flex-start")};
  background: ${(p) => (p.$out ? "var(--wa-bubble-out)" : "var(--wa-bubble-in)")};
  border-radius: ${(p) => (p.$out ? "12px 0 12px 12px" : "0 12px 12px 12px")};
  padding: 10px 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  box-shadow: 0 1px 0 rgba(14, 17, 22, 0.06);

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: rgba(14, 17, 22, 0.35);
    display: inline-block;
    animation: ${TypingFrame} 1.2s infinite ease-in-out;
  }
  span:nth-child(2) { animation-delay: 0.15s; }
  span:nth-child(3) { animation-delay: 0.3s; }
`;

/* Hook — runs the script when the component scrolls into view.
   Typing dots before EVERY message after the first, on the side that's about
   to speak. Optional loop with a pause between cycles. */
function useScriptedConversation(messages, autoPlay = true, loop = false, pace = 0) {
  const ref = useRef(null);
  const [step, setStep] = useState(autoPlay ? 0 : messages.length);
  const [typing, setTyping] = useState(null); // null | 'in' | 'out'
  const startedRef = useRef(!autoPlay);

  useEffect(() => {
    if (!autoPlay) return;
    const el = ref.current;
    if (!el) return;

    let active = true;
    let timers = [];

    const schedule = (fn, ms) => {
      const t = setTimeout(() => active && fn(), ms);
      timers.push(t);
    };

    const runCycle = () => {
      let cum = 0.4; // initial pause
      messages.forEach((m, i) => {
        if (i > 0) {
          const side = m.from; // 'in' or 'out'
          schedule(() => setTyping(side), cum * 1000);
          cum += 1.0; // typing duration — visible
          schedule(() => setTyping(null), cum * 1000);
          cum += 0.05;
        }
        schedule(() => setStep((s) => Math.max(s, i + 1)), cum * 1000);
        cum += 0.3 + pace; // breather between bubbles (+ optional pace)
      });

      if (loop) {
        schedule(() => {
          setStep(0);
          setTyping(null);
          schedule(runCycle, 600);
        }, (cum + 3.5) * 1000); // hold the finished thread for 3.5s before reset
      }
    };

    const start = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      runCycle();
    };

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            start();
            obs.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );
    obs.observe(el);

    return () => {
      active = false;
      timers.forEach(clearTimeout);
      obs.disconnect();
    };
  }, [messages, autoPlay, loop, pace]);

  return { ref, step, typing };
}

export default function ChatPreview({
  contact = { name: "Cliente", initial: "C", status: "en línea" },
  badge = null,
  messages = [],
  autoPlay = true,
  loop = false,
  pace = 0,
  style,
}) {
  const { ref, step, typing } = useScriptedConversation(messages, autoPlay, loop, pace);

  return (
    <Frame ref={ref} style={style}>
      <Header>
        <Avatar>{contact.initial}</Avatar>
        <NameBlock>
          <Name>{contact.name}</Name>
          <Status>{contact.status}</Status>
        </NameBlock>
        {badge && <Badge>{badge.label}</Badge>}
      </Header>

      <Body>
        {messages.map((m, i) => {
          const visible = i < step;
          return (
            <Row
              key={i}
              $out={m.from === "out"}
              initial={{ opacity: 0, y: 8, scale: 0.97 }}
              animate={visible ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
            >
              <Bubble $out={m.from === "out"}>
                {m.author && <span className="author">{m.author}</span>}
                <span className="body">{m.body}</span>
                <span className="meta">
                  {m.time}
                  {m.from === "out" && <Ticks read={m.read !== false} />}
                </span>
              </Bubble>
            </Row>
          );
        })}

        <AnimatePresence>
          {typing && (
            <TypingPill
              key={`typing-${typing}`}
              $out={typing === "out"}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22 }}
            >
              <span /><span /><span />
            </TypingPill>
          )}
        </AnimatePresence>
      </Body>
    </Frame>
  );
}
